// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { updateContext } from "@insite/client-framework/Context";
import {
    Handler,
    HasOnSuccess,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { Session, updateSession } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { chain, DispatchCompleteUpdateShipTo, GetDefaultShipTo, LoadShipTos, UpdateContext, UpdateSession } from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";


type HandlerType = Handler<
    {
        billTo: BillToModel;
        isEdit: boolean;
        // XNG-Change
        doNotUpdateShipto?: boolean;
    } & HasOnSuccess<boolean>,
    {
        shipTos: ShipToModel[];
        defaultShipTo: ShipToModel;
    }
>;

// XNG-Changes: in checkout shipping page we need to keep current ship to when bill to is updated
export const XngLoadShipTos: HandlerType = async props => {
    // <XNG-Change>
    if (props.parameter.doNotUpdateShipto) {
        return;
    }
    // </XNG-Change>
    const loadShipTosParameter: GetShipTosApiParameter = {
        billToId: props.parameter.billTo.id,
        expand: ["validation"],
        exclude: ["createNew", "oneTime", "showAll"],
    };

    const getShipTos = () => getShipTosDataView(props.getState(), loadShipTosParameter).value;
    let shipTos = getShipTos();
    if (!shipTos) {
        const awaitableLoadShipTos = makeHandlerChainAwaitable(loadShipTos);
        await awaitableLoadShipTos(loadShipTosParameter)(props.dispatch, props.getState);
        shipTos = getShipTos();
    }

    if (!shipTos?.length) {
        throw new Error("Could not find any shiptos for the selected billto.");
    }

    props.shipTos = shipTos;
};

export const XngGetDefaultShipTo: HandlerType = props => {
    // <XNG-Change>
    if (props.parameter.doNotUpdateShipto) {
        return;
    }
    // </XNG-Change>
    let defaultShipTo = props.shipTos.find(s => s.isDefault);

    if (!defaultShipTo) {
        defaultShipTo = props.shipTos.find(s => s.id === props.parameter.billTo.id);
        if (!defaultShipTo) {
            defaultShipTo = props.shipTos.filter(s => s.id !== props.parameter.billTo.id)[0];
        }
    }

    if (!defaultShipTo) {
        throw new Error("Could not find any shiptos for the selected billto.");
    }

    props.defaultShipTo = defaultShipTo;
};

export const XngUpdateContext: HandlerType = props => {
    // <XNG-Change>
    if (props.parameter.doNotUpdateShipto) {
        return;
    }
    // </XNG-Change>
    updateContext({
        billToId: props.parameter.billTo.id,
        shipToId: props.defaultShipTo.id,
        settings: getSettingsCollection(props.getState()),
    });
};

export const XngUpdateSession: HandlerType = async props => {
    // <XNG-Change>
    if (props.parameter.doNotUpdateShipto) {
        return;
    }
    // </XNG-Change>

    await updateSession({
        session: {
            billToId: props.parameter.billTo.id,
            shipToId: props.defaultShipTo.id,
            customerWasUpdated: true,
        } as Session,
    });
};

export const XngDispatchCompleteUpdateShipTo: HandlerType = props => {
    if (props.parameter.doNotUpdateShipto) {
        return;
    }
    props.dispatch({
        type: "Pages/CheckoutShipping/CompleteUpdateShipTo",
        shipTo: props.defaultShipTo,
    });
};

replaceInChain(chain, UpdateSession, XngUpdateSession);
replaceInChain(chain, GetDefaultShipTo, XngGetDefaultShipTo);
replaceInChain(chain, LoadShipTos, XngLoadShipTos);
replaceInChain(chain, UpdateContext, XngUpdateContext);
replaceInChain(chain, DispatchCompleteUpdateShipTo, XngDispatchCompleteUpdateShipTo);
